import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageTemplates } from "@/components/agendamentos/MessageTemplates";
import { useToast } from "@/hooks/use-toast";
import whatsappService from "@/services/whatsappService";
import { Owner } from "@/types";
import { DateTimeSelect } from "@/components/agendamentos/DateTimeSelect";
import { IntervalSelect } from "@/components/agendamentos/IntervalSelect";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { CampaignsList } from "./CampaignsList";

interface CampaignMenuProps {
  owners: Owner[];
}

export function CampaignMenu({ owners }: CampaignMenuProps) {
  const { toast } = useToast();
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date>();
  const [scheduledTime, setScheduledTime] = useState("");
  const [interval, setInterval] = useState("30days");
  const [isIntervalEnabled, setIsIntervalEnabled] = useState(true);
  const [isOwnerListOpen, setIsOwnerListOpen] = useState(false);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [editingCampaign, setEditingCampaign] = useState<any>(null);

  const handleOwnerSelect = (ownerId: string) => {
    setSelectedOwners(prev =>
      prev.includes(ownerId)
        ? prev.filter(id => id !== ownerId)
        : [...prev, ownerId]
    );
  };

  const handleTemplateSelect = (template: string) => {
    setMessage(template);
  };

  const handleSendCampaign = async () => {
    if (!selectedOwners.length) {
      toast({
        title: "Nenhum proprietário selecionado",
        description: "Selecione pelo menos um proprietário para enviar a mensagem.",
        variant: "destructive",
      });
      return;
    }

    if (!message.trim()) {
      toast({
        title: "Mensagem vazia",
        description: "Digite uma mensagem para enviar.",
        variant: "destructive",
      });
      return;
    }

    if (!scheduledDate || !scheduledTime) {
      toast({
        title: "Data e hora não definidas",
        description: "Selecione uma data e hora para a campanha.",
        variant: "destructive",
      });
      return;
    }

    const scheduledDateTime = new Date(`${scheduledDate.toDateString()} ${scheduledTime}`);
    if (scheduledDateTime < new Date()) {
      toast({
        title: "Data inválida",
        description: "A data e hora agendadas devem ser futuras.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSending(true);
      
      const newCampaign = {
        id: editingCampaign ? editingCampaign.id : Date.now().toString(),
        owners: owners.filter(owner => selectedOwners.includes(owner.id)),
        message,
        scheduledDate,
        scheduledTime,
        interval: isIntervalEnabled ? interval : null,
        status: 'pending' as const
      };

      if (editingCampaign) {
        setCampaigns(prev => prev.map(campaign => 
          campaign.id === editingCampaign.id ? newCampaign : campaign
        ));
        setEditingCampaign(null);
        toast({
          title: "Campanha atualizada",
          description: "Campanha atualizada com sucesso.",
        });
      } else {
        setCampaigns(prev => [...prev, newCampaign]);
        toast({
          title: "Campanha criada",
          description: `Campanha cadastrada com sucesso para ${selectedOwners.length} proprietário(s).`,
        });
      }

      // Reset form
      setSelectedOwners([]);
      setMessage("");
      setScheduledDate(undefined);
      setScheduledTime("");
      setInterval("30days");
      setIsIntervalEnabled(true);
      setIsOwnerListOpen(false);
    } catch (error) {
      toast({
        title: "Erro ao cadastrar campanha",
        description: "Ocorreu um erro ao cadastrar a campanha. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleUpdateCampaignStatus = (campaignId: string, status: 'pending' | 'sent' | 'failed') => {
    setCampaigns(prev => 
      prev.map(campaign => 
        campaign.id === campaignId 
          ? { ...campaign, status } 
          : campaign
      )
    );
  };

  const handleDeleteCampaign = (campaignId: string) => {
    setCampaigns(prev => prev.filter(campaign => campaign.id !== campaignId));
  };

  const handleEditCampaign = (campaign: any) => {
    setEditingCampaign(campaign);
    setSelectedOwners(campaign.owners.map((owner: any) => owner.id));
    setMessage(campaign.message);
    setScheduledDate(campaign.scheduledDate);
    setScheduledTime(campaign.scheduledTime);
    setInterval(campaign.interval || "30days");
    setIsIntervalEnabled(!!campaign.interval);
    setIsOwnerListOpen(true);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-6 p-6 bg-white rounded-xl shadow-card">
      <div>
        <h2 className="text-xl font-medium mb-6">
          {editingCampaign ? "Editar Campanha" : "Nova Campanha"}
        </h2>
        
        <div className="space-y-6">
          <Collapsible
            open={isOwnerListOpen}
            onOpenChange={setIsOwnerListOpen}
            className="border rounded-lg p-4"
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 hover:bg-gray-50 rounded-md">
              <span className="text-sm font-medium">
                Proprietários Selecionados ({selectedOwners.length})
              </span>
              {isOwnerListOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <ScrollArea className="h-[200px] rounded-md border p-4">
                <div className="space-y-3">
                  {owners.map((owner) => (
                    <div key={owner.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={owner.id}
                        checked={selectedOwners.includes(owner.id)}
                        onCheckedChange={() => handleOwnerSelect(owner.id)}
                      />
                      <label
                        htmlFor={owner.id}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {owner.name}
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CollapsibleContent>
          </Collapsible>

          <div className="space-y-4">
            <h3 className="text-sm font-medium">Mensagem da Campanha</h3>
            <MessageTemplates onSelectTemplate={handleTemplateSelect} />
            <div className="mt-4">
              <label className="text-sm text-gray-600 mb-2 block">Editar/Visualizar Mensagem</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Digite ou edite a mensagem da campanha aqui..."
                className="min-h-[120px]"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid gap-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="enableInterval"
                    checked={isIntervalEnabled}
                    onCheckedChange={(checked) => setIsIntervalEnabled(checked as boolean)}
                  />
                  <label
                    htmlFor="enableInterval"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Ativar intervalo de repetição
                  </label>
                </div>
                {isIntervalEnabled && (
                  <IntervalSelect
                    value={interval}
                    onChange={setInterval}
                  />
                )}
              </div>
              
              <DateTimeSelect
                date={scheduledDate}
                time={scheduledTime}
                onDateChange={setScheduledDate}
                onTimeChange={setScheduledTime}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              onClick={handleSendCampaign}
              disabled={isSending}
              className="flex-1"
            >
              {editingCampaign ? "Atualizar Campanha" : "Cadastrar Campanha"}
            </Button>
            {editingCampaign && (
              <Button
                variant="outline"
                onClick={() => {
                  setEditingCampaign(null);
                  setSelectedOwners([]);
                  setMessage("");
                  setScheduledDate(undefined);
                  setScheduledTime("");
                  setInterval("30days");
                  setIsIntervalEnabled(true);
                }}
              >
                Cancelar
              </Button>
            )}
          </div>
        </div>
      </div>

      <CampaignsList 
        campaigns={campaigns}
        onUpdateStatus={handleUpdateCampaignStatus}
        onDelete={handleDeleteCampaign}
        onEdit={handleEditCampaign}
      />
    </div>
  );
}

